import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { AxiosResponse } from "axios";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Divider } from "primereact/divider";
import { ProgressSpinner } from "primereact/progressspinner";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { TextInput } from "../../components/TextInput/TextInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { TextAreaInput } from "../../components/TextAreaInput/TextAreaInput";
import { RadioButtonInput } from "../../components/RadioButtonInput/RadioButtonInput";
import { WeighbridgeInput } from "../../components/WeighbridgeInput/WeighbridgeInput";
import { NewPartnerForm } from "../../forms/NewPartnerForm/NewPartnerForm";
import {
  DocumentOfReceive,
  ReceiveStatus,
} from "../../models/DocumentOfReceive";
import { Product, ProductType, ProductCategory } from "../../models/Product";
import { RoleType } from "../../models/Role";
import { Warehouse } from "../../models/Warehouse";
import { Partner } from "../../models/Partner";
import { Weighbridge } from "../../weight-bridge/Weighbridge";
import { getWeighbridge } from "../../weight-bridge/Weighbridge";
import { getRepromaterialInputTemplate } from "../../document-templates/RepromaterialInputTemplate";
import { useApplicationContext } from "../../application/ApplicationContext";
import { activeUser } from "../../services/AuthService";
import {
  addNewDocument,
  getOneDisposition,
  getOneDocument,
  updateOneDocument,
} from "../../services/NewVersionService";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllProducts } from "../../services/ProductService";
import { getAllProductCategories } from "../../services/ProductCategoryService";
import { getAllPartners } from "../../services/PartnerService";
import "./NewDocumentOfReceiveRepromaterialPage.scss";

export interface DocumentOfReceivePageProps {
  show?: string;
  buttonSaveStyle?: React.CSSProperties;
  buttonNewPartnerStyle?: React.CSSProperties;
  buttonResetStyle?: React.CSSProperties;
}

export const NewDocumentOfReceiveRepromaterialPage = (
  p: DocumentOfReceivePageProps,
) => {
  const { show, buttonSaveStyle, buttonNewPartnerStyle, buttonResetStyle } = p;

  const { t } = useTranslation();

  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === "new";

  const { state, serialPortSet, serialPortValueSet } = useApplicationContext();

  const unwrapArray = (resp: any): any[] => {
    const d = resp?.data;
    if (d?.result && Array.isArray(d.result)) return d.result;
    if (Array.isArray(d)) return d;
    return [];
  };

  const buildDefaultDocument = (docId?: string): DocumentOfReceive => ({
    id: docId ?? "",
    warehouse:
      activeUser?.role?.name === RoleType.Warehouseman
        ? (cloneDeep(activeUser.warehouse) as Warehouse)
        : ({} as Warehouse),
    warehouseman: `${activeUser?.firstName} ${activeUser?.lastName}`,
    type: undefined,
    date: new Date(),
    time: new Date(),
    repromaterialProducts: [],
    repromaterialProductsJSON: "",
    partner: {} as Partner,
    shipper: {} as Partner,
    driverName: "",
    vehicleNumber: "",
    note: "",
    dispositionId: "",
    foreignProduct: false,
    productOwner: {} as Partner,
    finishedDocument: false,
    finishedDocumentFirstTime: false,
    mercantile: false,
    input: true,
  });

  const [updating, setUpdating] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [productCategories, setProductCategories] = useState<ProductCategory[]>(
    [],
  );
  const [products, setProducts] = useState<Product[]>([]);

  const [newDocumentOfReceive, setNewDocumentOfReceive] =
    useState<DocumentOfReceive>(() => buildDefaultDocument(isNew ? "" : id));

  const [newProduct, setNewProduct] = useState<Product>({
    id: "",
    name: "",
    type: ProductType.RawMaterial,
    code: "",
    category: {} as ProductCategory,
    lot: "",
    amount: "",
    measuringUnit: "",
    germination: "",
    activeSubstance: "",
    expiryDate: undefined,
  });
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);

  const [productsDataTable, setProductsDataTable] = useState<Product[]>([]);
  const [okButtonDisabled, setOkButtonDisabled] = useState<boolean>(false);

  const [
    currentProductRequiresWeighbridge,
    setCurrentProductRequiresWeighbridge,
  ] = useState<boolean>(false);
  const [bruttoProductAmount, setBruttoProductAmount] = useState<number>();
  const [taraProductAmount, setTaraProductAmount] = useState<number>();
  const [inputBasedOnDisposition, setInputBasedOnDisposition] =
    useState<boolean>(false);
  const [disabledByInputDate, setDisabledByInputDate] =
    useState<boolean>(false);

  const [editingProductIndex, setEditingProductIndex] = useState<number | null>(
    null,
  );
  const [pdfData, setPdfData] = useState<any>(null);

  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        const [whResp, partnersResp, catResp, prodResp] = await Promise.all([
          getAllWarehouses(),
          getAllPartners(),
          getAllProductCategories(),
          getAllProducts(),
        ]);

        const wh = unwrapArray(whResp) as Warehouse[];
        const pa = unwrapArray(partnersResp) as Partner[];
        const ca = unwrapArray(catResp) as ProductCategory[];
        const pr = unwrapArray(prodResp) as Product[];

        setWarehouses(wh);
        setPartners(pa);
        setProductCategories(ca);
        setProducts(pr);

        // default filtered products
        setFilteredProducts(
          pr.filter((x) => x.type === ProductType.RawMaterial),
        );

        if (isNew) {
          const d = buildDefaultDocument("");
          setNewDocumentOfReceive(d);
          setPdfData(getRepromaterialInputTemplate(d, pr));
          setUpdating(false);
          setInputBasedOnDisposition(false);
          return;
        }

        const resp = await getOneDocument(id as string);
        const document: any = resp?.data?.result ?? resp?.data;
        console.log(document);
        if (!document) {
          // @ts-ignore
          toast.current?.show({
            severity: "error",
            detail: t("Greška pri učitavanju dokumenta"),
            life: 3000,
          });
          return;
        }

        // normalize dates
        if (document.date) {
          document.date = new Date(document.date);
          if (document.date.getTime() > new Date().getTime()) {
            setDisabledByInputDate(true);
          }
          if (document.status === null) document.date = new Date();
        }
        if (document.time) {
          document.time = new Date(document.time);
          if (document.status === null) document.time = new Date();
        }
        if (!document.warehouseman) {
          document.warehouseman = `${activeUser?.firstName} ${activeUser?.lastName}`;
        }

        if (
          document.dispositionId &&
          document.status !== ReceiveStatus.Arrived
        ) {
          setInputBasedOnDisposition(true);
          document.status = ReceiveStatus.Arrived;

          try {
            const oneDisposition = await getOneDisposition(
              document.dispositionId,
            );
            const allProductsJson = cloneDeep(
              oneDisposition.data.result
                ?.repromaterialDispositionProductsJSON as string,
            );

            const dispProducts = JSON.parse(allProductsJson);

            const repromaterialProducts = dispProducts
              .filter((pi: any) => parseFloat(pi?.amount) > 0)
              .map((pi: any) => {
                const p = pr.find((x) => x.id === pi?.id);
                return {
                  id: pi?.id ?? "",
                  type: ProductType.RawMaterial,
                  name: p?.name ?? "",
                  code: p?.name ?? "",
                  category: pi?.category as ProductCategory,
                  lot: "",
                  amount: pi?.amount ?? "",
                  measuringUnit: p?.measuringUnit ?? "",
                  germination: pi?.germination ?? "",
                  activeSubstance: pi?.activeSubstance ?? "",
                  expiryDate: pi?.expiryDate ?? undefined,
                };
              });

            document.repromaterialProductsJSON = allProductsJson;
            document.repromaterialProducts = repromaterialProducts;

            if (document.repromaterialProducts?.length > 0) {
              const firstProductId = document.repromaterialProducts[0].id;
              const productRequiresWeighbridge =
                pr.find((x) => x.id === firstProductId)?.isWeighbridge || false;
              setCurrentProductRequiresWeighbridge(productRequiresWeighbridge);
            }
          } catch (err) {
            console.error("Greška pri učitavanju dispozicija:", err);
          }
        } else {
          setInputBasedOnDisposition(false);
        }

        setNewDocumentOfReceive(document);
        setPdfData(getRepromaterialInputTemplate(document, pr));
        setUpdating(true);
      } catch (e) {
        console.error(e);
        // @ts-ignore
        toast.current?.show({
          severity: "error",
          detail: t("Došlo je do greške pri učitavanju podataka"),
          life: 3000,
        });
      } finally {
        setIsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    //create all products table
    let newProductsDataTable: Product[] = [];
    if (
      newDocumentOfReceive.repromaterialProducts &&
      newDocumentOfReceive.repromaterialProducts.length > 0
    ) {
      const allProducts = newDocumentOfReceive.repromaterialProducts ?? [];
      for (const productItem of allProducts) {
        const product = products.find((item) => item.id === productItem.id);
        newProductsDataTable.push({
          id: product?.id ?? "",
          type: ProductType.RawMaterial,
          name: products.find((item) => item.id === product?.id)
            ?.name as string,
          code: products.find((item) => item.id === product?.id)
            ?.name as string,
          category: product?.category as ProductCategory,
          lot: productItem?.lot ?? "",
          amount: productItem?.amount ?? "",
          measuringUnit: product?.measuringUnit ?? "",
          germination: productItem?.germination ?? "",
          activeSubstance: productItem?.activeSubstance ?? "",
          expiryDate: new Date(productItem?.expiryDate as Date) ?? undefined,
        });
      }
    }
    setProductsDataTable(newProductsDataTable);
  }, [newDocumentOfReceive.repromaterialProducts]);

  // populate pdf when data changes
  useEffect(() => {
    const newPdf = getRepromaterialInputTemplate(
      newDocumentOfReceive,
      products,
    );
    setPdfData(newPdf);
  }, [newDocumentOfReceive, products]);

  useEffect(() => {
    if (newProduct.category?.id === undefined) {
      setFilteredProducts(
        products.filter((x) => x.type === ProductType.RawMaterial),
      );
    } else {
      setFilteredProducts(
        products.filter((x) => x.category?.id == newProduct.category?.id),
      );
    }
  }, [newProduct]);

  //* -------------- WEIGHBRIDGE START ----------------
  const [weighbridge, setWeighbridge] = useState<Weighbridge>();
  const [readableStreamClosed, setReadableStreamClosed] = useState<any>();
  const readerRef = useRef<ReadableStreamDefaultReader | null>(null);
  const readableStreamClosedRef = useRef<Promise<void> | null>(
    Promise.resolve(),
  );

  const disconnectSerial = async () => {
    try {
      await readerRef.current?.cancel();
      await readerRef.current?.releaseLock();
      readerRef.current = null;

      await readableStreamClosedRef.current?.catch(() => {});
      readableStreamClosedRef.current = null;

      if (state.serialPort) await state.serialPort.close();
    } catch (e) {
      console.error(e);
    } finally {
      serialPortSet(undefined);
    }
  };

  const connectSerial = async (_weighbridge: Weighbridge) => {
    try {
      let _port;

      if (state.serialPort && state.serialPort.readable) {
        _port = state.serialPort;
      } else {
        _port = await window.navigator.serial.requestPort();
        serialPortSet(_port);
        await _port.open({ baudRate: _weighbridge.baudRate });
      }

      const decoder = new TextDecoderStream();
      const inputDone = _port.readable.pipeTo(decoder.writable);
      const lineReadableStream = new TransformStream(
        _weighbridge.getLineBreakTransformer(),
      );
      const newReadableStreamClosed =
        decoder.readable.pipeThrough(lineReadableStream);

      const lineReader = lineReadableStream.readable.getReader();

      setReadableStreamClosed(newReadableStreamClosed);

      readerRef.current = lineReader;
      readableStreamClosedRef.current = inputDone;

      let previousValue = "";
      while (true) {
        if (!_port || !_port.readable) continue;

        const { value, done } = await lineReader.read();
        if (done) {
          break;
        }

        if (previousValue !== value) {
          serialPortValueSet(value);
        }

        previousValue = value;
      }
    } catch (e) {
      console.error(e);
    } finally {
      await disconnectSerial();
    }
  };

  useEffect(() => {
    // Only attempt connection if both conditions are met
    if (
      newDocumentOfReceive.warehouse?.weighbridgeModel &&
      currentProductRequiresWeighbridge
    ) {
      const _weighbridge = getWeighbridge(
        newDocumentOfReceive.warehouse.weighbridgeModel,
      );

      if (!_weighbridge) {
        disconnectSerial();
        return;
      }

      setWeighbridge(_weighbridge);
      connectSerial(_weighbridge);
    } else {
      // Disconnect if conditions aren't met
      disconnectSerial();
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      disconnectSerial();
    };
  }, [
    newDocumentOfReceive.warehouse,
    currentProductRequiresWeighbridge,
    newProduct,
  ]);

  //* --------------- WEIGHBRIDGE END -----------------
  const toast = useRef(null);

  const handleNewDocumentOfReceive = (prop: string, value: any) => {
    setNewDocumentOfReceive((newDocumentOfReceive) => ({
      ...newDocumentOfReceive,
      [prop]: value,
    }));
  };

  const handleNewRegistrationNumber = (prop: string, value: any) => {
    setNewDocumentOfReceive((newDocumentOfReceive) => ({
      ...newDocumentOfReceive,
      [prop]: value.toUpperCase(),
    }));
  };

  const handlePartnerChange = (prop: string, value: any) => {
    let copyOfNewDocumentOfReceive = cloneDeep(newDocumentOfReceive);
    const partner = partners.find((item) => item.id == value);
    copyOfNewDocumentOfReceive.partner = partner;
    copyOfNewDocumentOfReceive.productOwner = partner;
    setNewDocumentOfReceive(copyOfNewDocumentOfReceive);
  };

  const handleShipperChange = (prop: string, value: any) => {
    let copyOfNewDocumentOfReceive = cloneDeep(newDocumentOfReceive);
    copyOfNewDocumentOfReceive.shipper = partners.find(
      (item) => item.id == value,
    );
    setNewDocumentOfReceive(copyOfNewDocumentOfReceive);
  };

  const handleWarehouseChange = (prop: string, value: any) => {
    let copyOfNewDocumentOfReceive = cloneDeep(newDocumentOfReceive);
    activeUser?.role?.name === RoleType.Warehouseman
      ? (copyOfNewDocumentOfReceive.warehouse = cloneDeep(
          activeUser.warehouse,
        ) as Warehouse)
      : (copyOfNewDocumentOfReceive.warehouse = warehouses.find(
          (item) => item.id == value,
        ) as Warehouse);
    setNewDocumentOfReceive(copyOfNewDocumentOfReceive);
  };

  const handleNewProduct = (prop: string, value: any) => {
    setNewProduct((newProduct) => ({
      ...newProduct,
      [prop]: value,
    }));
  };

  const handleCategoryChange = (prop: string, value: any) => {
    let copyOfNewProduct = cloneDeep(newProduct);
    copyOfNewProduct.category = productCategories.find(
      (category) => category.id === value,
    );
    setNewProduct(copyOfNewProduct);
  };

  const handleProductChange = (prop: string, value: any) => {
    let copyOfNewProduct = cloneDeep(newProduct);
    copyOfNewProduct.id = value;
    copyOfNewProduct.name = value;
    copyOfNewProduct.measuringUnit = filteredProducts.find(
      (product) => product.id === value,
    )?.measuringUnit;
    if (newProduct.category?.id === undefined) {
      copyOfNewProduct.category = filteredProducts.find(
        (product) => product.id === value,
      )?.category;
    }

    const selectedProduct = products.find((product) => product.id === value);
    setCurrentProductRequiresWeighbridge(
      selectedProduct?.isWeighbridge || false,
    );
    setNewProduct(copyOfNewProduct);
  };

  const handleProductChangeByWeightbridge = (prop: string, value: any) => {
    let copyOfNewProduct = cloneDeep(newProduct);
    if (prop === "brutto") {
      const newAmount = parseInt(value?.toString()) - (taraProductAmount ?? 0);
      copyOfNewProduct.amount = newAmount?.toString();
      setBruttoProductAmount(value);
    } else {
      const newAmount =
        (bruttoProductAmount ?? 0) - parseInt(value?.toString());
      copyOfNewProduct.amount = newAmount?.toString();
      setTaraProductAmount(value);
    }
    setNewProduct(copyOfNewProduct);
  };

  const addNewProduct = () => {
    if (newProduct.name === "" || newProduct.amount === "") {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: t(`Moraju biti popunjena polja za naziv i količinu proizvoda`),
        life: 3000,
      });
      return;
    }

    if ((newProduct.amount as string).indexOf(",") >= 0) {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: t(
          `Neispravan unos količine. Decimalni broj treba uneti sa . a ne sa ,`,
        ),
        life: 3000,
      });
      return;
    }

    if (!/^-?\d+(\.\d+)?$/.test(newProduct.amount as string)) {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: t(`Količina uneta u neispravnom formatu`),
        life: 3000,
      });
      return;
    }

    if (editingProductIndex !== null) {
      const copyOfNewDocumentOfReceive = cloneDeep(newDocumentOfReceive);
      if (copyOfNewDocumentOfReceive.repromaterialProducts) {
        copyOfNewDocumentOfReceive.repromaterialProducts[editingProductIndex] =
          cloneDeep(newProduct);
      }
      setNewDocumentOfReceive(copyOfNewDocumentOfReceive);
    } else {
      const copyOfNewDocumentOfReceive = cloneDeep(newDocumentOfReceive);
      const copyOfNewProduct = cloneDeep(newProduct);
      copyOfNewDocumentOfReceive.repromaterialProducts?.push(copyOfNewProduct);
      setNewDocumentOfReceive(copyOfNewDocumentOfReceive);
    }

    setFilteredProducts(
      products.filter((item) => item.type === ProductType.RawMaterial),
    );

    //reset newProductFields
    setNewProduct({
      id: "",
      name: "",
      type: ProductType.RawMaterial,
      code: "",
      category: {} as ProductCategory,
      lot: "",
      amount: "",
      measuringUnit: "",
      germination: "",
      activeSubstance: "",
      expiryDate: undefined,
    });
    setEditingProductIndex(null);
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    mode: "all",
  });

  const onSubmit = () => {
    if (newDocumentOfReceive.repromaterialProducts?.length === 0) {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: t(
          `Nije moguće snimiti prijemnicu. Nije unet ni jedan proizvod.`,
        ),
        life: 3000,
      });
      return;
    }
    setOkButtonDisabled(true);
    const copyOfNewDocumentOfReceive = cloneDeep(newDocumentOfReceive);
    copyOfNewDocumentOfReceive.repromaterialProductsJSON = JSON.stringify(
      newDocumentOfReceive.repromaterialProducts,
    );

    if (newDocumentOfReceive?.finishedDocumentFirstTime !== true) {
      copyOfNewDocumentOfReceive.date = new Date(); //ako nije zakljucena prijemnica prvi put,
      copyOfNewDocumentOfReceive.time = new Date(); //datum i vreme se formiraju kao trenutak zakljucenja prijemnice prvi put
    }

    if (newDocumentOfReceive.finishedDocument === true) {
      copyOfNewDocumentOfReceive.finishedDocumentFirstTime = true; //zakljucena prijemnica prvi put
    }

    if (!copyOfNewDocumentOfReceive.warehouseman) {
      copyOfNewDocumentOfReceive.warehouseman = `${activeUser?.firstName} ${activeUser?.lastName}`;
    }

    if (updating) {
      updateOneDocument(id as string, copyOfNewDocumentOfReceive).then(
        (response: AxiosResponse) => {
          if (response.status === 200 || response.status === 201) {
            //@ts-ignore
            toast.current.show({
              severity: "success",
              detail: t(`Uspešno popunjena prijemnica`),
              life: 3000,
            });
            navigate(`/repromaterial-input-fast`);
            setOkButtonDisabled(false);
          } else {
            //@ts-ignore
            toast.current.show({
              severity: "error",
              detail: t(
                `Dokument nije snimljen, proverite podatke o prijemu robe`,
              ),
              life: 3000,
            });
            setOkButtonDisabled(false);
          }
        },
      );
    } else {
      const payload: any = { ...cloneDeep(copyOfNewDocumentOfReceive) };

      // ako je new dokument, id mora biti undefined (nikako "")
      if (!payload.id || payload.id.trim() === "") {
        delete payload.id;
      }
      addNewDocument(payload).then((response: AxiosResponse) => {
        if (response.status === 200 || response.status === 201) {
          //@ts-ignore
          toast.current.show({
            severity: "success",
            detail: t(`Uspešno izmenjena prijemnica`),
            life: 3000,
          });
          navigate(`/repromaterial-input-fast`);
          setOkButtonDisabled(false);
        } else {
          //@ts-ignore
          toast.current.show({
            severity: "error",
            detail: t(
              `Dokument nije izmenjen, proverite podatke o prijemu robe`,
            ),
            life: 3000,
          });
          setOkButtonDisabled(false);
        }
      });
    }
  };

  const onError = () => {
    setOkButtonDisabled(true);
    //@ts-ignore
    toast.current.show({
      severity: "error",
      detail: t(`Neispravno popunjena polja`),
      life: 3000,
    });
    setOkButtonDisabled(false);
  };

  const handleResetForm = () => {
    setNewDocumentOfReceive(buildDefaultDocument(isNew ? "" : id));
    setNewProduct({
      id: "",
      name: "",
      type: ProductType.RawMaterial,
      code: "",
      category: {} as ProductCategory,
      lot: "",
      amount: "",
      measuringUnit: "",
      germination: "",
      activeSubstance: "",
      expiryDate: undefined,
    });
    setBruttoProductAmount(undefined);
    setTaraProductAmount(undefined);
    setCurrentProductRequiresWeighbridge(false);
    setEditingProductIndex(null);
  };

  let addButtonSaveStyle: React.CSSProperties = {};
  if (buttonSaveStyle) {
    addButtonSaveStyle = buttonSaveStyle;
  } else {
    addButtonSaveStyle.background = "rgba(0, 165, 83, 1)";
    addButtonSaveStyle.border = "none";
    addButtonSaveStyle.margin = "0 .5rem";
  }

  let addButtonNewPartnerStyle: React.CSSProperties = {};
  if (buttonNewPartnerStyle) {
    addButtonNewPartnerStyle = buttonNewPartnerStyle;
  } else {
    addButtonNewPartnerStyle.background = "rgba(21, 115, 71, 1)";
    addButtonNewPartnerStyle.border = "none";
  }

  let addButtonResetStyle: React.CSSProperties = {};
  if (buttonResetStyle) {
    addButtonResetStyle = buttonResetStyle;
  } else {
    addButtonResetStyle.background = "rgba(220, 53, 69, 1)";
    addButtonResetStyle.border = "none";
  }

  const nameOfProduct = (rowData: any) => {
    return rowData.name;
  };

  const nameOfCategory = (rowData: any) => {
    return rowData.category.name;
  };

  const editActionBodyTemplate = (rowData: any, options: any) => {
    return (
      <ButtonInput
        labelText=""
        icon="bi bi-pencil"
        onButtonClick={() => {
          setNewProduct(rowData);
          setEditingProductIndex(options.rowIndex);
          const productRequiresWeighbridge =
            products.find((p) => p.id === rowData.id)?.isWeighbridge || false;
          setCurrentProductRequiresWeighbridge(productRequiresWeighbridge);
        }}
        addStyle={{
          backgroundColor: "white",
          color: "rgba(21, 115, 71, 1)",
          border: "none",
        }}
      />
    );
  };

  if (isLoading) return <ProgressSpinner />;

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="form-wrapper">
        <div className="form-header">
          <div className="header-title">
            <i
              className="bi bi-truck header-title-icon"
              style={{ fontSize: 18 }}
            ></i>
            <span className="header-title">
              {`${t("Prijemnica broj")}:`}{" "}
              {newDocumentOfReceive?.finishedDocumentNumber ??
                newDocumentOfReceive.finishedDocumentId ??
                ""}
            </span>
          </div>
          <div className="header-buttons">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={t("Sve prijemnice")}
              icon="bi bi-arrow-left-square"
              onButtonClick={() => {
                navigate(`/repromaterial-input-fast`);
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={""}
              icon="bi bi-file-earmark-pdf"
              onButtonClick={() => {
                if (
                  newDocumentOfReceive.finishedDocument === true &&
                  newDocumentOfReceive.finishedDocumentId &&
                  newDocumentOfReceive.repromaterialProducts &&
                  newDocumentOfReceive.repromaterialProducts.length > 0 &&
                  newDocumentOfReceive.driverName !== "" &&
                  newDocumentOfReceive.vehicleNumber !== "" &&
                  newDocumentOfReceive.warehouseman !== ""
                ) {
                  if (downloadPdfDocument.current) {
                    // @ts-ignore
                    downloadPdfDocument.current();
                  }
                } else {
                  //@ts-ignore
                  toast.current.show({
                    severity: "error",
                    detail: t(
                      `Nije moguće preuzeti prijemnicu u kojoj nisu uneta sva obavezna polja, koja ne sadrži ni jedan proizvod u listi, koja nije zaključena i snimljena.`,
                    ),
                    life: 3000,
                  });
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
              }}
              labelText={""}
              icon="bi bi-printer"
              onButtonClick={() => {
                if (
                  newDocumentOfReceive.finishedDocument === true &&
                  newDocumentOfReceive.finishedDocumentId &&
                  newDocumentOfReceive.repromaterialProducts &&
                  newDocumentOfReceive.repromaterialProducts.length > 0 &&
                  newDocumentOfReceive.driverName !== "" &&
                  newDocumentOfReceive.vehicleNumber !== "" &&
                  newDocumentOfReceive.warehouseman !== ""
                ) {
                  if (printPdfDocument.current) {
                    // @ts-ignore
                    printPdfDocument.current();
                  }
                } else {
                  //@ts-ignore
                  toast.current.show({
                    severity: "error",
                    detail: t(
                      `Nije moguće odštampati prijemnicu u kojoj nisu uneta sva obavezna polja, koja ne sadrži ni jedan proizvod u listi, koja nije zaključena i snimljena.`,
                    ),
                    life: 5000,
                  });
                }
              }}
            />
          </div>
        </div>
        <div className="form-body">
          <div className="form-input-fields">
            {activeUser?.role?.name === RoleType.Warehouseman ? (
              <TextInput
                id="warehouse"
                value={activeUser?.warehouse?.name ?? ""}
                onValueChange={handleWarehouseChange}
                labelText={`${t("Magacin")} *`}
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={
                  activeUser?.role?.name === RoleType.Warehouseman ||
                  disabledByInputDate === true
                }
              />
            ) : (
              <DropdownInput
                id="warehouse"
                value={newDocumentOfReceive.warehouse.id}
                onValueChange={handleWarehouseChange}
                optionValue="id"
                optionLabel="name"
                options={warehouses.filter((item) => item.virtual !== true)}
                filter={false}
                labelText={`${t("Magacin")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={
                  //@ts-ignore
                  activeUser?.role?.name === RoleType.Warehouseman ||
                  disabledByInputDate === true
                }
              />
            )}
            <DateInput
              id="date"
              value={
                newDocumentOfReceive?.finishedDocumentFirstTime
                  ? (newDocumentOfReceive.date as Date)
                  : new Date()
              }
              onValueChange={handleNewDocumentOfReceive}
              labelText={`${t("Datum")} *`}
              placeholderText={t("Izaberite datum")}
              icon="bi bi-calendar-check"
              maxDate={new Date()}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={
                activeUser?.role?.name === RoleType.Warehouseman ||
                disabledByInputDate === true
              }
            />
            <TextInput
              id="warehouseman"
              value={newDocumentOfReceive.warehouseman as string}
              onValueChange={handleNewDocumentOfReceive}
              labelText={`${t("Magacioner")} *`}
              placeholderText={t("Unesite ime magacionera")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: false }}
              disabled={
                (activeUser?.role?.name === RoleType.Warehouseman &&
                  newDocumentOfReceive.finishedDocument === true) ||
                disabledByInputDate === true
              }
            />
            <DateInput
              id="time"
              value={
                newDocumentOfReceive?.finishedDocumentFirstTime
                  ? (newDocumentOfReceive.time as Date)
                  : new Date()
              }
              onValueChange={handleNewDocumentOfReceive}
              labelText={`${t("Vreme")} *`}
              placeholderText={t("Izaberite vreme")}
              timeOnly={true}
              icon="bi bi-clock"
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={
                activeUser?.role?.name === RoleType.Warehouseman ||
                disabledByInputDate === true
              }
            />
            <div className="divider-line">
              <Divider />
            </div>
            <DropdownInput
              id="partner"
              value={newDocumentOfReceive.partner?.id as string}
              optionValue="id"
              optionLabel="name"
              onValueChange={handlePartnerChange}
              options={partners}
              filter={true}
              labelText={`${t("Dobavljač")} *`}
              placeholderText={t("Izaberite iz liste")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={
                (activeUser?.role?.name === RoleType.Warehouseman &&
                  newDocumentOfReceive.finishedDocument === true) ||
                disabledByInputDate === true
              }
            />
            <div className="new-partner-button">
              <NewPartnerForm
                buttonLabel={`${t("Dodaj dobavljača")}`}
                buttonIcon="bi bi-plus-square"
                show={show}
                buttonStyle={{
                  border: "none",
                  background: "rgba(21, 115, 71, 1)",
                  color: "#ffffff",
                  marginRight: "1rem",
                  margin: "0 1rem 0 0",
                }}
                buttonNewCityStyle={{
                  border: "none",
                  background: "rgba(21, 115, 71, 1)",
                }}
                buttonSaveStyle={{
                  border: "none",
                  background: "rgba(0, 165, 83, 1)",
                }}
                buttonInactiveStyle={{
                  border: "none",
                  background: "rgba(108, 117, 125, 1)",
                }}
              />
            </div>
            <DropdownInput
              id="shipper"
              value={newDocumentOfReceive.shipper?.id as string}
              optionValue="id"
              optionLabel="name"
              onValueChange={handleShipperChange}
              options={partners}
              filter={true}
              labelText={t("Prevoznik")}
              placeholderText={t("Izaberite iz liste")}
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={
                (activeUser?.role?.name === RoleType.Warehouseman &&
                  newDocumentOfReceive.finishedDocument === true) ||
                disabledByInputDate === true
              }
            />
            <TextInput
              id="driverName"
              value={newDocumentOfReceive.driverName as string}
              onValueChange={handleNewDocumentOfReceive}
              labelText={`${t("Ime vozača")} *`}
              placeholderText={t("Unesite ime vozača")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={
                (activeUser?.role?.name === RoleType.Warehouseman &&
                  newDocumentOfReceive.finishedDocument === true) ||
                disabledByInputDate === true
              }
            />
            <TextInput
              id="vehicleNumber"
              value={(
                newDocumentOfReceive.vehicleNumber as string
              )?.toUpperCase()}
              onValueChange={handleNewRegistrationNumber}
              labelText={`${t("Registarski broj vozila")} *`}
              placeholderText={t("Unesite registarski broj")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{
                required: true,
              }}
              disabled={
                (activeUser?.role?.name === RoleType.Warehouseman &&
                  newDocumentOfReceive.finishedDocument === true) ||
                disabledByInputDate === true
              }
            />
            <TextInput
              id="shippingSlip"
              value={newDocumentOfReceive.shippingSlip as string}
              onValueChange={handleNewDocumentOfReceive}
              labelText={t("Otpremni listić")}
              placeholderText=""
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={
                (activeUser?.role?.name === RoleType.Warehouseman &&
                  newDocumentOfReceive.finishedDocument === true) ||
                disabledByInputDate === true
              }
            />
            <div className="divider-line">
              <Divider />
            </div>
            <>
              <DropdownInput
                id="category"
                value={newProduct.category?.id}
                onValueChange={handleCategoryChange}
                optionValue="id"
                optionLabel="name"
                options={productCategories.filter(
                  (x) => x.type == ProductType.RawMaterial,
                )}
                labelText={t("Tip proizvoda")}
                placeholderText={t("Izaberite tip proizvoda")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={
                  (activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfReceive.finishedDocument === true) ||
                  disabledByInputDate === true ||
                  inputBasedOnDisposition === true ||
                  newDocumentOfReceive.status === ReceiveStatus.Arrived
                }
              />
              <DropdownInput
                id="name"
                value={newProduct.id}
                optionValue="id"
                optionLabel="name"
                onValueChange={handleProductChange}
                options={filteredProducts}
                filter={true}
                labelText={t("Proizvod")}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={
                  (activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfReceive.finishedDocument === true) ||
                  disabledByInputDate === true ||
                  inputBasedOnDisposition === true ||
                  newDocumentOfReceive.status === ReceiveStatus.Arrived
                }
              />
              {products?.find((oneProduct) => oneProduct.id == newProduct?.id)
                ?.isWeighbridge &&
                newDocumentOfReceive?.warehouse?.weighbridgeModel && (
                  <WeighbridgeInput
                    id="brutto"
                    value={(bruttoProductAmount as number) ?? ""}
                    onValueChange={handleProductChangeByWeightbridge}
                    labelText={`${t("Bruto")} *`}
                    icon="bi bi-minecart-loaded"
                    iconAlign="right"
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    rules={{
                      required: true,
                      pattern: {
                        value: /^\d+$/,
                        message: "Please enter a number",
                      },
                    }}
                    weighbridge={
                      currentProductRequiresWeighbridge
                        ? weighbridge
                        : undefined
                    }
                  />
                )}
              {products?.find((oneProduct) => oneProduct.id == newProduct?.id)
                ?.isWeighbridge &&
                newDocumentOfReceive?.warehouse?.weighbridgeModel && (
                  <WeighbridgeInput
                    id="tara"
                    value={(taraProductAmount as number) ?? ""}
                    onValueChange={handleProductChangeByWeightbridge}
                    labelText={`${t("Tara")} *`}
                    icon="bi bi-minecart"
                    iconAlign="right"
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    rules={{
                      required: true,
                      pattern: {
                        value: /^\d+$/,
                        message: "Please enter a number",
                      },
                    }}
                    weighbridge={
                      currentProductRequiresWeighbridge
                        ? weighbridge
                        : undefined
                    }
                  />
                )}
              <div className="repro-product-details">
                {(newProduct.category?.name == "SAAT - Seme" ||
                  newProduct.category?.name == "PFLA - Hemija") && (
                  <TextInput
                    id="lot"
                    value={newProduct.lot as string}
                    onValueChange={handleNewProduct}
                    labelText={t("Partija (LOT)")}
                    placeholderText=""
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    disabled={
                      (activeUser?.role?.name === RoleType.Warehouseman &&
                        newDocumentOfReceive.finishedDocument === true) ||
                      disabledByInputDate === true
                    }
                  />
                )}
                {newProduct.category?.name == "SAAT - Seme" && (
                  <TextInput
                    id="germination"
                    value={newProduct.germination as string}
                    onValueChange={handleNewProduct}
                    labelText={t("Klijavost")}
                    placeholderText=""
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    disabled={
                      (activeUser?.role?.name === RoleType.Warehouseman &&
                        newDocumentOfReceive.finishedDocument === true) ||
                      disabledByInputDate === true
                    }
                  />
                )}
                {(newProduct.category?.name == "DUMI - Djubriva" ||
                  newProduct.category?.name == "PFLA - Hemija") && (
                  <TextInput
                    id="activeSubstance"
                    value={newProduct.activeSubstance as string}
                    onValueChange={handleNewProduct}
                    labelText={t("Aktivna supstanca")}
                    placeholderText=""
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    disabled={
                      (activeUser?.role?.name === RoleType.Warehouseman &&
                        newDocumentOfReceive.finishedDocument === true) ||
                      disabledByInputDate === true
                    }
                  />
                )}
                {newProduct.category?.name == "PFLA - Hemija" && (
                  <DateInput
                    id="expiryDate"
                    value={newProduct.expiryDate as Date}
                    onValueChange={handleNewProduct}
                    labelText={`${t("Datum isticanja")}`}
                    placeholderText=""
                    icon="bi bi-calendar-check"
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    disabled={disabledByInputDate === true}
                  />
                )}
                {newProduct.category?.name == "SAAT - Seme" && (
                  <div className="empty-field"></div>
                )}
                {newProduct.category?.name == "DUMI - Djubriva" && (
                  <>
                    <div className="empty-field"></div>
                    <div className="empty-field"></div>
                  </>
                )}
                {(newProduct.category?.name == undefined ||
                  newProduct.category?.name == "HILF - Pomocni materijal" ||
                  newProduct.category?.name == "AGCM - Merkantila") && (
                  <>
                    <div className="empty-field"></div>
                    <div className="empty-field"></div>
                    <div className="empty-field"></div>
                  </>
                )}
                <TextInput
                  id="amount"
                  value={newProduct.amount as string}
                  onValueChange={handleNewProduct}
                  labelText={t("Količina")}
                  placeholderText=""
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{
                    pattern: {
                      value: /\d+\.?\d*/,
                      message: "Please enter a number",
                    },
                  }}
                  disabled={
                    (activeUser?.role?.name === RoleType.Warehouseman &&
                      newDocumentOfReceive.finishedDocument === true) ||
                    disabledByInputDate === true
                  }
                />
                <TextInput
                  id="measuringUnit"
                  value={newProduct.measuringUnit as string}
                  onValueChange={handleNewProduct}
                  labelText={t("Jedinica mere")}
                  placeholderText=""
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={true}
                />
                <div className="add-product">
                  <ButtonInput
                    labelText={t("Dodaj proizvod")}
                    icon="bi bi-plus-square"
                    onButtonClick={addNewProduct}
                    addStyle={{
                      border: "none",
                      background: "rgba(21, 115, 71, 1)",
                      color: "#ffffff",
                      height: "2.6rem",
                    }}
                    disabled={
                      (activeUser?.role?.name === RoleType.Warehouseman &&
                        newDocumentOfReceive.finishedDocument === true) ||
                      disabledByInputDate === true
                    }
                  />
                </div>
              </div>
            </>
            <div className="data-table-container">
              <DataTable value={productsDataTable} loading={isLoading} lazy>
                <Column
                  body={nameOfProduct}
                  header={`${t(`Naziv proizvoda`)}:`}
                ></Column>
                <Column
                  body={nameOfCategory}
                  header={`${t("Tip proizvoda")}:`}
                ></Column>
                <Column field="lot" header={`${t("Partija")}:`}></Column>
                <Column field="amount" header={`${t("Količina")}:`}></Column>
                <Column field="measuringUnit" header={`${t("M.J:")}`}></Column>
                <Column
                  body={editActionBodyTemplate}
                  className="datatable-button-column"
                  header={t("Opcije")}
                />
              </DataTable>
            </div>
            <RadioButtonInput
              id="foreignProduct"
              value={newDocumentOfReceive.foreignProduct}
              radioButtonValue1={t("DA")}
              radioButtonValue2={t("NE")}
              onValueChange={handleNewDocumentOfReceive}
              labelText={t("Tuđa roba na skladištenju")}
              control={control}
              errors={errors}
              disabled={
                activeUser?.role?.name === RoleType.Warehouseman &&
                newDocumentOfReceive?.status === ReceiveStatus.Finished
              }
            />
            <div className="note-field">
              <TextAreaInput
                id="note"
                value={newDocumentOfReceive.note as string}
                onValueChange={handleNewDocumentOfReceive}
                rows={2}
                cols={50}
                labelText={t("Napomena")}
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={
                  (activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfReceive.finishedDocument === true) ||
                  disabledByInputDate === true
                }
              />
            </div>
            <RadioButtonInput
              id="finishedDocument"
              value={newDocumentOfReceive.finishedDocument}
              radioButtonValue1={t("DA")}
              radioButtonValue2={t("NE")}
              onValueChange={handleNewDocumentOfReceive}
              labelText={t("Zaključena prijemnica")}
              control={control}
              errors={errors}
              disabled={
                (activeUser?.role?.name === RoleType.Warehouseman &&
                  newDocumentOfReceive.finishedDocument === true) ||
                activeUser?.role?.name === RoleType.Logistician ||
                disabledByInputDate === true
              }
            />
            <div className="footer-buttons">
              <ButtonInput
                labelText={t("Sačuvaj")}
                onButtonClick={handleSubmit(onSubmit, onError)}
                addStyle={addButtonSaveStyle}
                disabled={disabledByInputDate === true || okButtonDisabled}
              />
              <ButtonInput
                labelText={t("Resetuj")}
                onButtonClick={handleResetForm}
                addStyle={addButtonResetStyle}
                disabled={
                  (activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfReceive.finishedDocument === true) ||
                  disabledByInputDate === true
                }
              />
            </div>
          </div>
          <div className="documents-preview">
            <PdfPreview
              pdfDefinition={pdfData}
              downloadDocument={downloadPdfDocument}
              printDocument={printPdfDocument}
              documentName={`Prijemnica_${
                newDocumentOfReceive.finishedDocumentId ?? ""
              }`}
            />
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
